import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { Status } from "src/Pages/Company/Absences/AbsenceRequest/AbsenceRequest.models";
import { IEmployeeAssignmentsDTO } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import { IParsedBalancesFormData } from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsModifyBalance/AbsenceRecordsModifyBalanceForm";
import {
  IEmployeeGroup,
  IOrganizationUnit,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import {
  editEmployeeAssignments,
  fetchAbsenceTypeBalances,
  fetchEmployee,
  fetchEmployeeAbsencePlan,
  fetchEmployeeAbsences,
  fetchEmployeeAssignments,
  fetchEmployeeBalances,
  fetchEmployeeGroups,
  fetchEmployeesRecords,
  fetchOrganizationUnits,
  modifyEmployeeBalances,
  uploadMedia,
} from "./api";
import { AbsenceQueryKeys } from "./config";

const EmployeeQueries = {
  employee: {
    byId: (employeeId: string | undefined, enabled: boolean = true) =>
      queryOptions({
        enabled: enabled && !!employeeId,
        queryKey: AbsenceQueryKeys.employee(employeeId!),
        queryFn: () => fetchEmployee(employeeId!),
      }),
  },
  employeeBalances: {
    byId: (employeeId?: string, absenceTypeId?: string) =>
      queryOptions({
        enabled: !!employeeId,
        queryKey: AbsenceQueryKeys.employeeBalances(employeeId, absenceTypeId),
        queryFn: ({ signal }) => fetchEmployeeBalances(employeeId!, { signal }),
      }),
  },
  absenceTypeBalances: {
    byId: (
      employeeId: string,
      billingCycles: string[],
      absenceTypeId?: string
    ) =>
      queryOptions({
        enabled: !!absenceTypeId && billingCycles.length > 0,
        queryKey: AbsenceQueryKeys.absenceTypeBalances(
          employeeId,
          absenceTypeId,
          billingCycles
        ),
        queryFn: ({ signal }) =>
          fetchAbsenceTypeBalances({
            employeeId,
            absenceTypeId,
            billingCycles,
            signal,
          }),
      }),
  },
  employeeAbsences: {
    byParams: (params: {
      employeeId: string | undefined;
      from?: string;
      to?: string;
      statuses: Status[];
    }) =>
      queryOptions({
        enabled: !!params.employeeId,
        queryKey: AbsenceQueryKeys.absences(params),
        queryFn: () =>
          fetchEmployeeAbsences({
            employeeId: params.employeeId!,
            from: params.from ?? "",
            to: params.to ?? "",
            statuses: params.statuses,
          }),
        retry: 0,
      }),
  },
  employeeAbsencePlan: {
    byId: (employeeId: string, year: string) =>
      queryOptions({
        enabled: !!employeeId,
        queryKey: AbsenceQueryKeys.absencePlans({ employeeId, year }),
        queryFn: ({ signal }) =>
          fetchEmployeeAbsencePlan({ employeeId, year, signal }),
        retry: 0,
      }),
  },
  employeesRecords: (params: {
    companyId: string;
    currentYear: string;
    page?: number;
    size?: number;
    sort?: string[];
    filters?: { field: string; value: string | string[] }[];
  }) =>
    queryOptions({
      queryKey: AbsenceQueryKeys.employeesRecords(params),
      queryFn: ({ signal }) => fetchEmployeesRecords({ ...params, signal }),
      placeholderData: (prev) => prev,
    }),
  uploadMedia: mutationOptions({
    mutationFn: (file: File) => uploadMedia(file),
    onSuccess: () => {
      dynamicNotification(__("Plik został pomyślnie przesłany!"), "success");
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas przesyłania pliku."),
        "error",
        "top-right",
        7000
      );
    },
  }),

  modifyEmployeeBalances: (employeeId: string) =>
    mutationOptions({
      mutationFn: (formData: IParsedBalancesFormData) =>
        modifyEmployeeBalances(employeeId, formData),
    }),

  editAssignments: (employeeId: string) =>
    mutationOptions({
      mutationFn: (formData: IEmployeeAssignmentsDTO) =>
        editEmployeeAssignments(employeeId, formData),
    }),

  employeeGroups: (companyId: string) =>
    queryOptions<IEmployeeGroup[]>({
      queryKey: AbsenceQueryKeys.employeeGroups(companyId),
      queryFn: () => fetchEmployeeGroups(companyId),
    }),

  organizationUnits: (companyId: string) =>
    queryOptions<IOrganizationUnit[]>({
      queryKey: AbsenceQueryKeys.organizationUnits(companyId),
      queryFn: () => fetchOrganizationUnits(companyId),
    }),

  employeeAssignments: (employeeId: string) =>
    queryOptions({
      queryKey: AbsenceQueryKeys.employeeAssignments(employeeId),
      queryFn: () => fetchEmployeeAssignments(employeeId),
    }),
};

export const useSetAssingmentsForEmployee = (employeeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...EmployeeQueries.editAssignments(employeeId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: AbsenceQueryKeys.employeeRecordsAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: AbsenceQueryKeys.employeePatterns(employeeId),
        }),
      ]);
    },
  });
};

export const useModifyEmployeeBalances = (employeeId: string) =>
  useMutation(EmployeeQueries.modifyEmployeeBalances(employeeId));

export const useUploadAttachment = () =>
  useMutation(EmployeeQueries.uploadMedia);

export const useFetchEmployeeAssignments = (employeeId: string) =>
  useQuery(EmployeeQueries.employeeAssignments(employeeId));

export const useFetchEmployee = (employeeId?: string, enabled?: boolean) =>
  useQuery(EmployeeQueries.employee.byId(employeeId, enabled));

export const useFetchEmployeeBalances = (
  employeeId?: string,
  absenceTypeId?: string
) => useQuery(EmployeeQueries.employeeBalances.byId(employeeId, absenceTypeId));

export const useFetchAbsenceTypeBalance = (
  employeeId: string,
  billingCycles: string[],
  absenceTypeId?: string
) =>
  useQuery(
    EmployeeQueries.absenceTypeBalances.byId(
      employeeId,
      billingCycles,
      absenceTypeId
    )
  );

export const useFetchEmployeeAbsences = (params: {
  employeeId: string | undefined;
  from?: string;
  to?: string;
  statuses: Status[];
}) => useQuery(EmployeeQueries.employeeAbsences.byParams(params));

export const useFetchAbsencePlan = (employeeId: string, year: string) =>
  useQuery(EmployeeQueries.employeeAbsencePlan.byId(employeeId, year));

export const useFetchEmployeesRecords = (params: {
  companyId: string;
  currentYear: string;
  page?: number;
  size?: number;
  sort?: string[];
  filters?: { field: string; value: string | string[] }[];
}) => useQuery(EmployeeQueries.employeesRecords(params));

export const useFetchEmployeeGroups = (companyId: string) =>
  useQuery(EmployeeQueries.employeeGroups(companyId));

export const useFetchOrganizationUnits = (companyId: string) =>
  useQuery(EmployeeQueries.organizationUnits(companyId));

export default EmployeeQueries;
